/**
 * What changed in each release, newest first, for the Update Log (main menu).
 * Written for players in both languages (not through i18n: these are whole
 * notes, not UI strings). Add an entry with every release that players notice.
 */
export interface ChangelogEntry {
  version: string;
  /** YYYY-MM-DD. */
  date: string;
  en: string[];
  th: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.19.1',
    date: '2026-10-29',
    en: [
      'Keep fighting: after a win, "Next" starts the next monster (story stage or tower floor) without going back to the notebook.',
      'Fight Monsters has its own bookmark again, right under Heroes, with the next story fight at the top.',
    ],
    th: [
      'สู้ต่อได้เลย: ชนะแล้วกด "ตัวต่อไป" เพื่อสู้มอนตัวถัดไป (ด่านเนื้อเรื่องหรือชั้นหอคอย) โดยไม่ต้องกลับไปที่สมุด',
      'หน้าสู้มอนกลับมามีที่คั่นของตัวเองแล้ว อยู่ใต้ฮีโร่ พร้อมปุ่มสู้ด่านถัดไปด้านบน',
    ],
  },
  {
    version: '0.19.0',
    date: '2026-10-27',
    en: [
      'Start Adventure now opens your heroes first, so you pick who plays before anything else.',
      'Tidier notebook: the bookmarks go Heroes, Quests, Shop, PVP, Contest, Collection, Options.',
      'Collection keeps My Monsters, the Monster Wiki and your Diary together behind one bookmark.',
      'A cleaner screen: hero stats fold away until you have points to spend, and How to Play lives in Options.',
    ],
    th: [
      'กด Start Adventure แล้วเข้าหน้าฮีโร่ก่อน เลือกตัวที่จะเล่นได้ทันที',
      'จัดสมุดใหม่ให้เป็นระเบียบ: ฮีโร่ เควสต์ ร้านค้า PVP ประกวด คอลเลกชัน ตัวเลือก',
      'คอลเลกชันรวมมอนสเตอร์ของฉัน วิกิมอนสเตอร์ และไดอารี่ไว้ในที่เดียว',
      'หน้าจอโล่งขึ้น: ค่าสเตตัสพับเก็บไว้จนกว่าจะมีแต้มให้อัป และวิธีเล่นย้ายไปอยู่ในตัวเลือก',
    ],
  },
  {
    version: '0.18.1',
    date: '2026-10-23',
    en: [
      'Drawings can be much bigger: up to 1,200 strokes (was 400).',
      'A full page can still be fixed: the eraser keeps working when the pens stop.',
      'No more dark fog flickering over the screen; the drawing page stays clear.',
    ],
    th: [
      'วาดได้ละเอียดขึ้นมาก: สูงสุด 1,200 เส้น (เดิม 400)',
      'หน้ากระดาษเต็มแล้วก็ยังแก้ได้: ยางลบยังใช้ได้แม้ปากกาวาดต่อไม่ได้',
      'แก้หมอกดำที่กะพริบบนหน้าจอ หน้าวาดใสสะอาดตลอด',
    ],
  },
  {
    version: '0.18.0',
    date: '2026-10-23',
    en: ['Hero slots are cheaper with coins: 200, 200, 300, 400, 500… and you can now have up to 12 heroes.', 'This Update Log: see what is new any time from the main menu.'],
    th: ['ช่องฮีโร่ถูกลงเมื่อใช้เหรียญ: 200, 200, 300, 400, 500… และมีฮีโร่ได้สูงสุด 12 ตัวแล้ว', 'ปุ่มบันทึกอัปเดตนี้: ดูได้ตลอดว่ามีอะไรใหม่ จากหน้าเมนูหลัก'],
  },
  {
    version: '0.17.0',
    date: '2026-10-23',
    en: [
      'Monsters give tells before some rounds… and sometimes bluff!',
      'Bosses charge a big hit every few rounds and get angry at half HP.',
      'Page events: Gold Star, Ink Rain, Double Page, Paper Cut and Mixed-up Hands.',
      'Ink Strike: trace a shape after some won rounds for an extra hit (can be switched off in Settings).',
    ],
    th: [
      'มอนสเตอร์ส่งท่าบอกใบ้ก่อนบางรอบ… แต่บางทีก็หลอก!',
      'บอสชาร์จท่าใหญ่ทุกไม่กี่รอบ และโกรธเมื่อเลือดเหลือครึ่ง',
      'เหตุการณ์หน้ากระดาษ: ดาวทอง ฝนหมึก หน้าคู่ กระดาษบาด ปุ่มสลับที่',
      'มินิเกมลากหมึก: บางรอบที่ชนะ ลากตามรูปเพื่อตีเพิ่ม (ปิดได้ในหน้าตั้งค่า)',
    ],
  },
  {
    version: '0.16.0',
    date: '2026-10-23',
    en: ['Leaving a battle half-way (reloading, closing the page) now counts as a loss.', 'Three losses in a row hurt your hero: it rests for 10 minutes while another hero plays.'],
    th: ['ออกจากการต่อสู้กลางคัน (รีเฟรช/ปิดหน้า) นับว่าแพ้', 'แพ้ติดกัน 3 ครั้ง ฮีโร่จะบาดเจ็บ ต้องพัก 10 นาที ระหว่างนั้นเล่นฮีโร่ตัวอื่นได้'],
  },
  {
    version: '0.15.1',
    date: '2026-10-22',
    en: ['The drawing page fits one phone screen: every tool without scrolling.'],
    th: ['หน้าวาดบนมือถือพอดีจอ เห็นเครื่องมือครบไม่ต้องเลื่อน'],
  },
  {
    version: '0.15.0',
    date: '2026-10-22',
    en: ['New brushes: marker, crayon, spray, watercolour and broad nib.', 'Pixel art mode with a 64, 32 or 16 square grid and its own paint bucket.'],
    th: ['แปรงใหม่: ปากกาเมจิก สีเทียน สเปรย์ สีน้ำ ปากกาหัวตัด', 'โหมดพิกเซลอาร์ต ตาราง 64 / 32 / 16 ช่อง พร้อมถังสีแบบช่อง'],
  },
  {
    version: '0.14.0',
    date: '2026-10-22',
    en: ['Ink gauge: fill it in battle to unleash your class ultimate.', 'Combo chain: win three rounds in a row with three different hands for a bonus hit.'],
    th: ['เกจหมึก: สะสมให้เต็มแล้วปล่อยท่าไม้ตายประจำสาย', 'คอมโบ: ชนะ 3 รอบติดด้วยท่าไม่ซ้ำกัน ได้ตีเพิ่ม'],
  },
  {
    version: '0.13.0',
    date: '2026-10-22',
    en: ['Quick match: if nobody is looking for a battle, you meet a bot copy of a real player.', 'Contest pictures open big; vote counts show after you vote.'],
    th: ['จับคู่ด่วน: ถ้าไม่มีใครหาคู่ จะได้สู้กับบอทที่ก็อปฮีโร่ของผู้เล่นจริง', 'ภาพในการประกวดกดดูใหญ่ได้ และเห็นจำนวนโหวตหลังโหวตแล้ว'],
  },
  {
    version: '0.12.0',
    date: '2026-10-22',
    en: ['Rankings: stars, level, Homework Tower and fastest story run.', 'Gear sorted into weapons, armour and charms.', 'Lighter on the graphics card.'],
    th: ['กระดานจัดอันดับ: ดาว เลเวล หอคอยการบ้าน และเวลาจบเนื้อเรื่อง', 'อุปกรณ์แยกเป็นอาวุธ ชุดเกราะ เครื่องราง', 'กินการ์ดจอน้อยลง'],
  },
  {
    version: '0.11.0',
    date: '2026-10-22',
    en: ['Shopping cart in the Special Shelf.', 'A paper fortune teller that really folds open and shut.'],
    th: ['ตะกร้าสินค้าในชั้นพิเศษ', 'กระดาษพับทายใจที่เปิดปิดเหมือนของจริง'],
  },
  {
    version: '0.10.0',
    date: '2026-10-22',
    en: ['Background music.'],
    th: ['เพลงประกอบ'],
  },
  {
    version: '0.9.0',
    date: '2026-10-22',
    en: ['The paper fortune teller, themed sets and more hero slots.'],
    th: ['กระดาษพับทายใจ ชุดธีม และช่องฮีโร่เพิ่ม'],
  },
];
